import Phaser from 'phaser';
import { coinLabel } from '../../config/coinLabel';
import { saveStorage } from '../saveStorage';
import { ProgressService } from '../../services/progressService';
import { LAST_LEVEL_ID, WIN_GATE_LEVEL_IDS } from '../../config/levels';
import { getAudioService } from '../audio/AudioService';
import { addMuteButton } from '../audio/audioControls';
import { addFullscreenButton } from '../screenControls';
import { installDebugReturn } from '../debugMode';
import { drawStarfield } from '../starfield';
import { COIN_SPIN_FRAMES, spinCoin } from '../coin';
import { LEADERBOARD_GREEN_HEX } from '../chapterTabs';
import { drawAvatarIcon } from '../avatarIcon';
import { SHOP_AVATARS, type AvatarIcon } from '../../config/avatars';

/**
 * AvatarShopScene — the eight icons you buy rather than unlock.
 *
 * One page, eight slots, no scrolling: that was the requirement and eight fit
 * comfortably four across. Each slot is name / icon / price, and turns into a
 * green OWNED the moment it is bought, exactly as the tower Shop marks a tower
 * you already have.
 *
 * Reached from the Leaderboards tab, under EDIT AVATAR, and only while signed
 * in — an icon you cannot wear is not worth 500 coins.
 *
 * Spec: docs/superpowers/specs/2026-09-21-avatar-shop-design.md
 */

const GOLD = 0xf5c518;
const GOLD_HEX = '#f5c518';

/** Slot geometry — four across, two down, centred on the 1280×720 canvas. */
const COLS = 4;
const SLOT_W = 250;
const SLOT_H = 226;
const COL_PITCH = 280;
const ROW_PITCH = 250;
const GRID_CX = 640;
const GRID_TOP_CY = 300;
/** The icon box inside a slot — a box, not a square: the title logo is wide. */
const ICON_W = 150;
const ICON_H = 78;

export class AvatarShopScene extends Phaser.Scene {
  private progress!: ProgressService;
  private slots?: Phaser.GameObjects.Container;
  private balanceText?: Phaser.GameObjects.Text;
  private hint?: Phaser.GameObjects.Text;
  private confirmOpen = false;

  constructor() {
    super({ key: 'AvatarShopScene' });
  }

  create(): void {
    this.progress = new ProgressService(saveStorage(), LAST_LEVEL_ID, WIN_GATE_LEVEL_IDS);
    this.confirmOpen = false;

    drawStarfield(this, 1280, 720);

    this.add
      .text(640, 48, 'AVATAR SHOP', {
        fontSize: '38px',
        color: LEADERBOARD_GREEN_HEX,
        fontStyle: 'bold',
      })
      .setOrigin(0.5);
    this.add
      .text(640, 84, 'BUY AN ICON, THEN WEAR IT FROM EDIT AVATAR', {
        fontSize: '15px',
        color: '#9aa6ad',
      })
      .setOrigin(0.5);

    const backBtn = this.add
      .text(80, 50, '← BACK', {
        fontSize: '22px',
        color: '#cfe0bd',
        backgroundColor: '#15131f',
        padding: { x: 16, y: 12 },
      })
      .setOrigin(0.5)
      .setName('avatar-shop-back')
      .setInteractive({ useHandCursor: true });
    backBtn.on('pointerup', () => this.scene.start('AvatarScene'));

    // The spinning coin and the balance, same as the menus the player came from.
    const coin = this.add.image(540, 128, 'coin').setDisplaySize(30, 30);
    spinCoin(this, coin, COIN_SPIN_FRAMES);
    this.balanceText = this.add
      .text(562, 128, '', { fontSize: '22px', color: GOLD_HEX, fontStyle: 'bold' })
      .setOrigin(0, 0.5);

    addMuteButton(this, 1245, 50, getAudioService(this));
    addFullscreenButton(this, 1100, 50);

    this.buildSlots();
    installDebugReturn(this); // Esc → DemoScene when launched via ?debug
  }

  /** Redrawn whole after a purchase — the one place slot state is decided. */
  private buildSlots(): void {
    this.slots?.destroy();
    this.slots = this.add.container(0, 0);
    this.balanceText?.setText(coinLabel(this.progress.coins()));

    SHOP_AVATARS.forEach((icon, i) => {
      const col = i % COLS;
      const row = Math.floor(i / COLS);
      const cx = GRID_CX + (col - (COLS - 1) / 2) * COL_PITCH;
      const cy = GRID_TOP_CY + row * ROW_PITCH;
      this.slots!.add(this.buildSlot(icon, cx, cy));
    });
  }

  /** One slot: name on top, icon, then BUY <price> or OWNED. */
  private buildSlot(icon: AvatarIcon, cx: number, cy: number): Phaser.GameObjects.Container {
    const c = this.add.container(cx, cy);
    const owned = this.progress.hasAvatarIcon(icon.code!);
    const price = icon.price!;
    const affordable = this.progress.coins() >= price;

    const plate = this.add.graphics();
    plate.fillStyle(0x1b2430, 0.92);
    plate.fillRoundedRect(-SLOT_W / 2, -SLOT_H / 2, SLOT_W, SLOT_H, 12);
    plate.lineStyle(2, owned ? 0x4caf50 : 0x2f3d4c, 1);
    plate.strokeRoundedRect(-SLOT_W / 2, -SLOT_H / 2, SLOT_W, SLOT_H, 12);
    c.add(plate);

    // The name. Long ones (The Hambi Generic) scale down rather than overrun
    // the plate — the Linux CI fonts are wider than this Mac's.
    const name = this.add
      .text(0, -SLOT_H / 2 + 26, icon.label, {
        fontSize: '19px',
        color: '#e8ecef',
        fontStyle: 'bold',
      })
      .setOrigin(0.5);
    if (name.width > SLOT_W - 24) name.setScale((SLOT_W - 24) / name.width);
    c.add(name);

    const art = drawAvatarIcon(this, icon.key, ICON_W, ICON_H);
    if (art) c.add(art.setPosition(0, -6));

    if (owned) {
      c.add(
        this.add
          .text(0, SLOT_H / 2 - 36, 'OWNED', {
            fontSize: '24px',
            color: '#4caf50',
            fontStyle: 'bold',
          })
          .setOrigin(0.5)
          .setName(`avatar-shop-owned-${icon.code}`),
      );
      return c;
    }

    // BUY <price> coins — gold when affordable, dimmed but still tappable when
    // not, so it can say why rather than simply ignoring the tap.
    const btnY = SLOT_H / 2 - 36;
    const btn = this.add
      .rectangle(0, btnY, 200, 46, affordable ? GOLD : 0x2a2a3a)
      .setStrokeStyle(2, affordable ? 0xfff3b0 : 0x555a66)
      .setName(`avatar-shop-buy-${icon.code}`)
      .setInteractive({ useHandCursor: affordable });
    c.add(btn);

    const label = this.add
      .text(0, btnY, `BUY ${price}`, {
        fontSize: '18px',
        color: affordable ? '#1a140a' : '#7f8c8d',
        fontStyle: 'bold',
      })
      .setOrigin(0, 0.5);
    const coin = this.add.image(0, btnY, 'coin').setDisplaySize(22, 22);
    // Centre the label+coin pair inside the button.
    const totalW = label.width + 6 + 22;
    label.setX(-totalW / 2);
    coin.setX(-totalW / 2 + label.width + 6 + 11);
    c.add(label);
    c.add(coin);

    if (affordable) {
      btn.on('pointerover', () => btn.setFillStyle(0xffd83a));
      btn.on('pointerout', () => btn.setFillStyle(GOLD));
    }
    btn.on('pointerup', () => {
      if (this.confirmOpen) return;
      if (affordable) this.showConfirm(icon);
      else this.flashHint('Not enough coins — win episodes to earn more.');
    });

    return c;
  }

  /** Purchase confirm — the tower Shop's modal, same shape and same reason. */
  private showConfirm(icon: AvatarIcon): void {
    const W = 1280;
    const H = 720;
    const D = 100;
    this.confirmOpen = true;
    const objects: Phaser.GameObjects.GameObject[] = [];

    objects.push(
      this.add
        .rectangle(W / 2, H / 2, W, H, 0x000000, 0.7)
        .setInteractive()
        .setDepth(D),
    );
    objects.push(
      this.add
        .rectangle(W / 2, H / 2, 560, 250, 0x1a1726, 1)
        .setStrokeStyle(2, GOLD)
        .setDepth(D + 1),
    );
    objects.push(
      this.add
        .text(W / 2, H / 2 - 72, `Buy ${icon.label}?`, {
          fontSize: '28px',
          color: '#ffffff',
          fontStyle: 'bold',
        })
        .setOrigin(0.5)
        .setDepth(D + 2),
    );
    objects.push(
      this.add
        .text(
          W / 2,
          H / 2 - 20,
          `${icon.price} Hambi coins · one-time purchase.\nWear it from EDIT AVATAR.`,
          { fontSize: '16px', color: '#9aa6ad', align: 'center', lineSpacing: 4 },
        )
        .setOrigin(0.5)
        .setDepth(D + 2),
    );

    const close = (): void => {
      for (const o of objects) o.destroy();
      this.confirmOpen = false;
    };

    const yes = this.add
      .rectangle(W / 2 - 100, H / 2 + 66, 170, 52, GOLD)
      .setStrokeStyle(2, 0xfff3b0)
      .setName('avatar-shop-confirm')
      .setInteractive({ useHandCursor: true })
      .setDepth(D + 2);
    objects.push(yes);
    objects.push(
      this.add
        .text(W / 2 - 100, H / 2 + 66, 'BUY', {
          fontSize: '20px',
          color: '#1a140a',
          fontStyle: 'bold',
        })
        .setOrigin(0.5)
        .setDepth(D + 3),
    );
    yes.on('pointerup', () => {
      const bought = this.progress.purchaseAvatarIcon(icon.code!, icon.price!);
      close();
      if (bought) {
        this.buildSlots();
        this.flashHint(`${icon.label} bought — wear it from EDIT AVATAR.`, '#8fd18f');
      } else {
        // Only reachable if the balance moved underneath us.
        this.flashHint('Could not buy that — check your coins.');
      }
    });

    const no = this.add
      .rectangle(W / 2 + 100, H / 2 + 66, 170, 52, 0x2a2a3a)
      .setStrokeStyle(2, 0x555a66)
      .setName('avatar-shop-cancel')
      .setInteractive({ useHandCursor: true })
      .setDepth(D + 2);
    objects.push(no);
    objects.push(
      this.add
        .text(W / 2 + 100, H / 2 + 66, 'CANCEL', {
          fontSize: '20px',
          color: '#cfd3dc',
          fontStyle: 'bold',
        })
        .setOrigin(0.5)
        .setDepth(D + 3),
    );
    no.on('pointerup', close);
  }

  /** Transient footer line (bought / can't afford). */
  private flashHint(msg: string, color = '#e8a0a0'): void {
    this.hint?.destroy();
    this.hint = this.add
      .text(640, 690, msg, { fontSize: '15px', color, fontStyle: 'italic' })
      .setOrigin(0.5)
      .setDepth(50);
    this.time.delayedCall(2200, () => {
      this.hint?.destroy();
      this.hint = undefined;
    });
  }
}
